/**
 * What a page looks like to the rail, and how the rail groups a list of them.
 *
 * The list itself is not here. It lives in `src/routes/-nav.ts`, beside the
 * pages it names, so adding a page touches the routes tree and not the
 * framework. This module is framework: it holds only the shape and the two
 * pure functions the rail reads it with.
 */
/** A group of employees a page can be limited to in the rail. */
export type PageAudience = 'engineering'

export interface InternalPage {
  id: string
  path: string
  title: string
  /**
   * Groups this page under a named, collapsible section of the rail —
   * "CS Tools", say. Two pages sharing a group name render under one shared
   * disclosure, in the order they appear in this list.
   *
   * **Optional in this type, required by the console's rail.** The field
   * stays optional because `groupInternalPages` still answers for a caller
   * with flat rows, but `src/routes/__root.tsx` reads the sections alone: a
   * page here with no group reaches its URL and appears nowhere in the rail.
   * `src/routes/-nav.test.ts` fails on one, which is what makes the omission
   * a red gate rather than a missing row somebody notices later.
   */
  group?: string
  /**
   * Shows this page in the rail only to a viewer in this audience. Absent
   * means every employee.
   *
   * A display choice, not access control. The rail hides the entry, and the
   * handlers behind the page refuse everybody else.
   */
  audience?: PageAudience
  /**
   * False keeps this page out of the rail. Absent means true.
   *
   * For a page that opens only from a link, because it needs an id the rail
   * cannot supply. The page is still declared, so `-nav.test.ts` still knows
   * every route file is there on purpose.
   */
  inRail?: boolean
}

/** One named section of the rail: a group's title, and the pages under it, in declared order. */
export interface InternalNavSection {
  group: string
  pages: InternalPage[]
}

/** The rail reserves this display section for Alerts and Platform pages. */
export const OPERATIONS_GROUP = 'Operations'
const OPERATION_SOURCE_GROUPS = new Set(['Alerts', 'Platform'])

/** Keep operational links together after the report sections. */
export function navSectionsWithOperations(
  sections: readonly InternalNavSection[]
): InternalNavSection[] {
  const operations = sections.filter(section =>
    OPERATION_SOURCE_GROUPS.has(section.group)
  )
  const reports = sections.filter(
    section => !OPERATION_SOURCE_GROUPS.has(section.group)
  )
  return operations.length === 0
    ? reports
    : [
        ...reports,
        {
          group: OPERATIONS_GROUP,
          pages: operations.flatMap(section => section.pages)
        }
      ]
}

/** Apply saved section order. Unknown names have no effect; new sections remain visible. */
export function orderNavSections(
  sections: readonly InternalNavSection[],
  order: readonly string[]
): InternalNavSection[] {
  const pinned = sections.find(section => section.group === OPERATIONS_GROUP)
  const movable = sections.filter(section => section.group !== OPERATIONS_GROUP)
  const rank = new Map(order.map((group, index) => [group, index]))
  const sorted = movable.map((section, index) => ({ section, index }))
  sorted.sort(
    (a, b) =>
      (rank.get(a.section.group) ?? order.length + a.index) -
      (rank.get(b.section.group) ?? order.length + b.index)
  )
  return [...sorted.map(item => item.section), ...(pinned ? [pinned] : [])]
}

/** Move one section by one place. The operations section stays last. */
export function moveNavSection(
  sections: readonly InternalNavSection[],
  group: string,
  direction: -1 | 1,
  savedOrder: readonly string[] = []
): string[] {
  const names = sections
    .filter(section => section.group !== OPERATIONS_GROUP)
    .map(section => section.group)
  const from = names.indexOf(group)
  const to = from + direction
  if (from < 0 || to < 0 || to >= names.length)
    return savedOrder.length ? [...savedOrder] : names
  ;[names[from], names[to]] = [names[to], names[from]]
  const visible = new Set(names)
  let next = 0
  const merged = [...new Set(savedOrder)].map(name =>
    visible.has(name) ? names[next++] : name
  )
  return [...merged, ...names.slice(next)]
}

/**
 * Split the declared page list into the rows a nav renders flat and the
 * sections it renders as a collapsible disclosure.
 *
 * Grouped and returned in one pass, in declaration order, rather than with a
 * second sort afterwards — `pages.ts` declares the whole rail's order
 * already, and a nav that regrouped it would only need to undo a sort this
 * function never did.
 *
 * @param pages - The declared page list, or a subset of it in tests.
 * @returns The flat rows, and the grouped sections, both in declared order.
 */
export function groupInternalPages(pages: readonly InternalPage[]): {
  ungrouped: InternalPage[]
  sections: InternalNavSection[]
} {
  const ungrouped: InternalPage[] = []
  const sections: InternalNavSection[] = []
  const byGroup = new Map<string, InternalPage[]>()

  for (const page of pages) {
    if (!page.group) {
      ungrouped.push(page)
      continue
    }
    const existing = byGroup.get(page.group)
    if (existing) {
      existing.push(page)
      continue
    }
    const created = [page]
    byGroup.set(page.group, created)
    sections.push({ group: page.group, pages: created })
  }

  return { ungrouped, sections }
}

/**
 * The pages the rail offers: every declared page but a link-only one.
 *
 * @param pages - The declared page list.
 * @returns The pages to group into the rail, in declared order.
 */
export function railPages(pages: readonly InternalPage[]): InternalPage[] {
  return pages.filter(page => page.inRail !== false)
}

/**
 * Compare two route paths without tripping over a trailing slash or a query.
 *
 * The rail marks the active row with this, so `/payments/ach-returns/` and
 * `/payments/ach-returns?window=7` must both match the declared path.
 *
 * @param pathname - The path to normalize.
 * @returns The path without its query string or trailing slash.
 */
export function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0]

  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

/**
 * The sections the rail shows once the reader's hidden pages are taken out.
 *
 * The current page stays, hidden or not, so a deep link never lands on a page
 * the rail cannot name. A section left with no page is dropped rather than
 * shown as an empty header. A hidden id that names no page changes nothing.
 *
 * @param sections - Every declared section.
 * @param hiddenPages - The page ids the reader hid.
 * @param activePath - The current path, already normalized.
 * @returns The sections to render, in declared order.
 */
export function visibleNavSections(
  sections: readonly InternalNavSection[],
  hiddenPages: readonly string[],
  activePath: string
): InternalNavSection[] {
  if (hiddenPages.length === 0) {
    return [...sections]
  }
  const hidden = new Set(hiddenPages)

  return sections
    .map(section => ({
      group: section.group,
      pages: section.pages.filter(
        page => !hidden.has(page.id) || normalizePath(page.path) === activePath
      )
    }))
    .filter(section => section.pages.length > 0)
}

/**
 * The sections a viewer's audiences allow, before the reader's own choices.
 *
 * A page with no audience stays for every viewer. A section left with no page
 * is dropped, so a viewer never sees a header for pages they cannot open.
 *
 * @param sections - Every declared section.
 * @param audiences - The audiences the viewer belongs to.
 * @returns The sections to offer this viewer, in declared order.
 */
export function audienceNavSections(
  sections: readonly InternalNavSection[],
  audiences: readonly PageAudience[]
): InternalNavSection[] {
  return sections
    .map(section => ({
      group: section.group,
      pages: section.pages.filter(
        page => !page.audience || audiences.includes(page.audience)
      )
    }))
    .filter(section => section.pages.length > 0)
}

/** How many of a section's pages the reader shows, out of how many it declares. */
export interface SectionVisibility {
  shown: number
  total: number
}

/**
 * Count a section's shown pages.
 *
 * The count reads the reader's choice, not what the rail draws. The current
 * page stays on screen while hidden, and still counts as hidden.
 *
 * @param section - One declared section.
 * @param hiddenPages - The page ids the reader hid.
 * @returns The shown count and the declared total.
 */
export function sectionVisibility(
  section: InternalNavSection,
  hiddenPages: readonly string[]
): SectionVisibility {
  const hidden = new Set(hiddenPages)
  const total = section.pages.length

  return {
    shown: section.pages.filter(page => !hidden.has(page.id)).length,
    total
  }
}

/**
 * The hidden pages after the reader flips a whole section.
 *
 * A section that shows any page hides all of them. A section with every page
 * hidden shows all of them. Pages outside the section keep their choice, and
 * the section's pages keep their declared order in the list.
 *
 * @param section - The section the reader flipped.
 * @param hiddenPages - The page ids the reader hid.
 * @returns The new hidden list.
 */
export function toggleSectionHidden(
  section: InternalNavSection,
  hiddenPages: readonly string[]
): string[] {
  const ids = new Set(section.pages.map(page => page.id))
  const others = hiddenPages.filter(id => !ids.has(id))
  const { shown } = sectionVisibility(section, hiddenPages)

  return shown === 0 ? others : [...others, ...ids]
}

/** Return hidden page ids after showing every page in the viewer's available sections. */
export function hiddenAfterShowingAvailablePages(
  sections: readonly InternalNavSection[],
  hiddenPages: readonly string[]
): string[] {
  const available = new Set(
    sections.flatMap(section => section.pages.map(page => page.id))
  )
  return hiddenPages.filter(id => !available.has(id))
}
